import { useRef, useState, useEffect, } from 'react';
import _ from 'lodash';
import ContentEditable from 'react-contenteditable';
import { useSnackbar, } from 'notistack';
import {
    Box,
    ClickAwayListener,
    Grid,
    IconButton,
    Stack,
    Typography,
    Tooltip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFirestore, } from '../../hooks';
import { escapeHTML, unescapeHTML, } from '../../utils';

const ListTerms = ({ document, filteredTerms, openEditableTerm, setOpenEditableTerm, sx = [], ...props }) => {
    const editableEng = useRef(null);
    const editableRus = useRef(null);
    const [editableTerm, setEditableTerm] = useState(null);

    const { updateDocument, } = useFirestore('sets');

    const { enqueueSnackbar, } = useSnackbar();

    const numTerms = document?.terms.length;
    const isPositiveTermsFav = document?.terms.some((term) => term.fav);
    const termsTypeLearningCards = document?.modes.learningCards.termsType;
    const progressServerLearningCards = document?.modes.learningCards.progress;
    const termsTypeWriteCards = document?.modes.writeCards.termsType;
    const progressServerWriteCards = document?.modes.writeCards.progress;

    const termStyles = (index) => {
        return {
            main: {
                bgcolor: editableTerm === index && 'common.white',
                border: editableTerm === index && 1,
                borderColor: editableTerm === index && 'common.orange',
                borderRadius: editableTerm === index && 1,
                color: (theme) => theme.palette.mode === 'light' ? editableTerm === index ? 'common.black' : 'text.primary' : editableTerm === index ? 'common.black' : 'common.pattensBlue',
                fontSize: 20,
                position: 'relative',
                px: editableTerm === index && 1,
                py: editableTerm === index && 0.5,
                overflowWrap: 'anywhere',
                '&:focus': {
                    outline: 'none',
                },
            },
            before: {
                color: (theme) => theme.palette.mode === 'light' ? 'text.primary' : 'common.pattensBlue',
                fontSize: 9,
                position: 'absolute',
            },
        }
    };

    const handleChangeTerm = (e, termLanguage) => {
        const text = e.target.value;
        const textUnescapeHTML = unescapeHTML(text);

        if (textUnescapeHTML.length > 150) {
            if (termLanguage === 'eng') {
                editableEng.current = textUnescapeHTML.slice(0, 150);
            } else if (termLanguage === 'rus') {
                editableRus.current = textUnescapeHTML.slice(0, 150);
            }

            enqueueSnackbar('Вы превысили допустимый лимит в 150 символов', { variant: 'warning', });

            return false;
        } else {
            if (termLanguage === 'eng') {
                editableEng.current = textUnescapeHTML;
            } else if (termLanguage === 'rus') {
                editableRus.current = textUnescapeHTML;
            }
        }
    };

    const handleCutTerm = (e) => {
        enqueueSnackbar('Вырезание текста запрещено', { variant: 'warning', });
        e.preventDefault();
    };

    const handleCopyTerm = (e) => {
        enqueueSnackbar('Копирование текста запрещено', { variant: 'warning', });
        e.preventDefault();
    };

    const handlePasteTerm = (e) => {
        enqueueSnackbar('Вставка текста запрещена', { variant: 'warning', });
        e.preventDefault();
    };

    const handleTermFav = (documentId, terms, index) => {
        let updatedTerms;

        const updateTerms = (terms, index, updatedTerm) => {
            const copyTerms = [...terms];
            copyTerms[index] = updatedTerm;

            updatedTerms = copyTerms;
        };

        if (terms[index]?.fav) {
            const copyWithoutFav = {
                ...terms[index],
            };

            delete copyWithoutFav.fav;

            updateTerms(terms, index, copyWithoutFav);
        } else {
            const termWithFav = {
                ...terms[index],
                fav: true,
            };

            updateTerms(terms, index, termWithFav);
        }

        updateDocument(documentId, { terms: updatedTerms, });
    };

    const changeProgressTermDelete = (documentId, mode, progressMode, numTerms) => {
        if (numTerms - progressMode > 1) {
            return;
        }

        updateDocument(documentId, { [mode]: progressMode - 1, })
    };

    const handleTermDelete = async (documentId, terms, termId) => {
        const filteredTerms = terms.filter((term) => term.id !== termId);

        const res = await updateDocument(documentId, { terms: filteredTerms, });

        if (res) {
            enqueueSnackbar('Карточка удалена', { variant: 'info', });

            changeProgressTermDelete(documentId, 'modes.learningCards.progress', progressServerLearningCards, numTerms);
            changeProgressTermDelete(documentId, 'modes.writeCards.progress', progressServerWriteCards, numTerms);
        }
    };

    const handleTermClickAway = (documentTerms) => {
        if (editableEng.current || editableRus.current) {
            const cloneTerms = _.cloneDeep(documentTerms);

            const serverIndex = cloneTerms.findIndex((term) => term.id === filteredTerms[editableTerm].id);

            cloneTerms[serverIndex] = {
                ...cloneTerms[serverIndex],
                id: cloneTerms[serverIndex].id,
                eng: escapeHTML(editableEng?.current) || cloneTerms[serverIndex].eng,
                rus: escapeHTML(editableRus?.current) || cloneTerms[serverIndex].rus,
            };

            updateDocument(document.id, { terms: cloneTerms, });
        }

        editableEng.current = null;
        editableRus.current = null;
        setEditableTerm(null);
        setOpenEditableTerm(false);
    };

    useEffect(() => {
        if (!isPositiveTermsFav) {
            if (termsTypeLearningCards === 'fav') {
                updateDocument(document.id, { 'modes.learningCards.termsType': 'all', })
            }
            if (termsTypeWriteCards === 'fav') {
                updateDocument(document.id, { 'modes.writeCards.termsType': 'all', })
            }
        }
    }, [isPositiveTermsFav]);

    return (
        <Box
            {...props}
            sx={[...(Array.isArray(sx) ? sx : [sx]),]}>
            {
                filteredTerms.map((term, index, terms) => {
                    const termContainer = (
                        <Grid
                            container
                            sx={{
                                border: 1,
                                borderRadius: 1,
                                borderStyle: editableTerm === index ? 'dashed' : 'solid',
                                borderColor: (theme) => theme.palette.mode === 'light' ? 'borderColor.light' : 'common.pattensBlue',
                                bgcolor: (theme) => theme.palette.mode === 'light'
                                    ? (index % 2) ? 'grey.50' : 'grey.200'
                                    : (index % 2) ? 'common.rhino' : 'common.shark',
                                mb: (index === terms.length - 1) ? 0 : [1.75, 2, 2.25, 2.5],
                                pt: [3, 6.5],
                                pr: 2.5,
                                pb: [editableTerm === index ? 3.75 : 3.25, editableTerm === index ? 5.25 : 4.75],
                                pl: [3.5, 4],
                            }}
                        >
                            <Grid item xs={12} sm={4} sx={{ order: [1, 0], }}>
                                <Typography
                                    component={ContentEditable}
                                    html={term.eng}
                                    disabled={editableTerm === null || editableTerm !== index}
                                    onChange={(e) => handleChangeTerm(e, 'eng')}
                                    onCut={handleCutTerm}
                                    onCopy={handleCopyTerm}
                                    onPaste={handlePasteTerm}
                                    sx={{
                                        ...termStyles(index).main,
                                        fontWeight: editableTerm === index ? 400 : 700,
                                        mt: [1.75, 0],
                                        '&::before': {
                                            ...termStyles(index).before,
                                            content: '"Original (ENG):"',
                                            fontWeight: 400,
                                            top: -18,
                                        },
                                    }} />
                            </Grid>
                            <Grid item xs={12} sm={5} sx={{ order: [1, 0], }}>
                                <Typography
                                    component={ContentEditable}
                                    html={term.rus}
                                    disabled={editableTerm === null || editableTerm !== index}
                                    onChange={(e) => handleChangeTerm(e, 'rus')}
                                    onCut={handleCutTerm}
                                    onCopy={handleCopyTerm}
                                    onPaste={handlePasteTerm}
                                    sx={{
                                        ...termStyles(index).main,
                                        fontStyle: editableTerm === index ? 'normal' : 'italic',
                                        mt: [4, 0],
                                        ml: [0, 4],
                                        '&::before': {
                                            ...termStyles(index).before,
                                            content: '"Перевод (РУС):"',
                                            fontStyle: 'normal',
                                            top: [editableTerm === index ? -18 : -12, editableTerm === index ? -18 : -14],
                                        },
                                    }} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Stack
                                    direction='row'
                                    justifyContent='flex-end'
                                    sx={{
                                        mt: -1.5,
                                        '& .MuiIconButton-root': {
                                            color: (theme) => theme.palette.mode === 'light' ? 'text.primary' : 'common.pattensBlue',
                                            p: 0,
                                            mt: [0.75, 0],
                                            mb: [2, 0],
                                            '&:hover': {
                                                bgcolor: 'transparent',
                                            },
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: [18, 19, 19, 20],
                                        },
                                    }}>
                                    <Tooltip
                                        title={(term?.fav) ? 'Удалить карточку из избранного' : 'Добавить карточку в избранное'}
                                        placement='top'
                                        arrow>
                                        <Box component='span' sx={{ mr: 0.75 }}>
                                            <IconButton onClick={() => handleTermFav(document.id, terms, index)}>
                                                <StarIcon sx={{
                                                    color: term?.fav && 'common.gold',
                                                    '&:hover': {
                                                        color: 'common.gold',
                                                    },
                                                }} />
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title='Редактировать карточку' placement='top' arrow>
                                        <Box component='span' sx={{ mr: 0.75 }}>
                                            <IconButton disabled={editableTerm === index} onClick={() => {
                                                setEditableTerm(index);
                                                setOpenEditableTerm(true);
                                            }}>
                                                <ModeEditIcon sx={{
                                                    '&:hover': {
                                                        color: 'secondary.main',
                                                    },
                                                }} />
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title={(document.terms.length === 2) ? 'Невозможно удалить карточку' : 'Удалить карточку'} placement='top' arrow>
                                        <Box component='span'>
                                            <IconButton disabled={openEditableTerm || document.terms.length === 2} onClick={() => handleTermDelete(document.id, terms, term.id)}>
                                                <DeleteIcon sx={{
                                                    '&:hover': {
                                                        color: 'secondary.main',
                                                    },
                                                }} />
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                </Stack>
                            </Grid>
                        </Grid>
                    );

                    return (
                        (index === editableTerm)
                            ? <ClickAwayListener key={term.id} onClickAway={() => handleTermClickAway(document.terms)}>
                                {termContainer}
                            </ClickAwayListener>
                            : <Box key={term.id}>{termContainer}</Box>
                    );
                })
            }
        </Box>
    );
};

export default ListTerms;